/**
=========================================================
* Annotation Portal React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useEffect, useState } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-table components
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";

// @mui material components
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";

// Annotation Portal React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDPagination from "components/MDPagination";

// Annotation Portal React example components
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

function DataTable({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  pagination,
  isSorted,
  noEndBorder,
  totalRecords,
}) {
  const defaultValue = entriesPerPage.defaultValue ? entriesPerPage.defaultValue : 10;
  const entries = entriesPerPage.entries
    ? entriesPerPage.entries.map((el) => el.toString())
    : ["5", "10", "15", "20", "25"];
  const columns = useMemo(() => table.columns, [table]);
  const data = useMemo(() => table.rows, [table]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0 },
      // Don't reset pagination/sort just because the parent passed a new
      // `table` object reference on render — keeps gotoPage(last) from
      // snapping back to page 0.
      autoResetPage: false,
      autoResetSortBy: false,
      autoResetGlobalFilter: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  // Set the default value for the entries per page when component mounts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setPageSize(defaultValue || 10), []);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value) => {
    setPageSize(value);
    // Call custom onChange handler if provided (for server-side pagination)
    if (entriesPerPage && entriesPerPage.onChange) {
      entriesPerPage.onChange(value);
    }
  };

  // Calculate total pages for server-side pagination
  const isServerSidePagination = totalRecords !== undefined && pagination?.onPageChange;
  const totalPages = isServerSidePagination
    ? Math.ceil(totalRecords / pageSize)
    : pageOptions.length;
  const currentPage = isServerSidePagination
    ? pagination?.page !== undefined
      ? pagination.page
      : pageIndex
    : pageIndex;

  // Generate page numbers for server-side or client-side pagination
  const serverPageOptions = isServerSidePagination
    ? Array.from({ length: totalPages }, (_, i) => i)
    : pageOptions;

  // Build a windowed page list: always show first, last, and currentPage ± 1,
  // inserting ellipsis strings between non-consecutive entries.
  const getPageWindow = () => {
    if (totalPages <= 7) return serverPageOptions;
    const pages = new Set([0, totalPages - 1]);
    for (let p = currentPage - 1; p <= currentPage + 1; p++) {
      if (p >= 0 && p < totalPages) pages.add(p);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    let prev = -1;
    for (const p of sorted) {
      if (prev !== -1 && p - prev > 1) result.push(`ellipsis-${p}`);
      result.push(p);
      prev = p;
    }
    return result;
  };

  // Render the paginations
  const renderPagination = getPageWindow().map((item) => {
    if (typeof item === "string") {
      return (
        <MDTypography
          key={item}
          variant="button"
          color="secondary"
          mx={0.5}
          sx={{ userSelect: "none", lineHeight: 1 }}
        >
          …
        </MDTypography>
      );
    }
    return (
      <MDPagination
        item
        key={item}
        onClick={() => {
          if (isServerSidePagination && pagination?.onPageChange) {
            pagination.onPageChange(null, item);
          } else {
            gotoPage(Number(item));
          }
        }}
        active={currentPage === item}
      >
        {item + 1}
      </MDPagination>
    );
  });

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // A function that sets the sorted value for the table
  const setSortedValue = (column) => {
    let sortedValue;

    if (isSorted && column.isSorted) {
      sortedValue = column.isSortedDesc ? "desc" : "asce";
    } else if (isSorted) {
      sortedValue = "none";
    } else {
      sortedValue = false;
    }

    return sortedValue;
  };

  // Total rows after filtering
  // Use totalRecords prop for server-side pagination, otherwise use rows.length for client-side
  const totalEntries = totalRecords !== undefined ? totalRecords : rows.length;

  // Start index (1-based)
  const entriesStart = totalEntries === 0 ? 0 : pageIndex * pageSize + 1;

  // End index (never exceed total rows)
  const entriesEnd =
    totalEntries === 0 ? 0 : Math.min(entriesStart + page.length - 1, totalEntries);

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {entriesPerPage || canSearch ? (
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
          {entriesPerPage && (
            <MDBox display="flex" alignItems="center">
              <Autocomplete
                disableClearable
                value={pageSize.toString()}
                options={entries}
                onChange={(event, newValue) => {
                  setEntriesPerPage(parseInt(newValue, 10));
                }}
                size="small"
                sx={{ width: "5rem" }}
                renderInput={(params) => <MDInput {...params} />}
              />
              <MDTypography variant="caption" color="secondary">
                &nbsp;&nbsp;entries per page
              </MDTypography>
            </MDBox>
          )}
          {canSearch && (
            <MDBox sx={{ minWidth: "10rem", flex: 1, maxWidth: "18rem" }} ml="auto">
              <MDInput
                placeholder="Search..."
                value={search}
                size="small"
                fullWidth
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                }}
                InputProps={
                  search
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              edge="end"
                              onClick={() => {
                                setSearch("");
                                onSearchChange("");
                              }}
                            >
                              <Icon sx={{ fontSize: "1rem !important" }}>close</Icon>
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            </MDBox>
          )}
        </MDBox>
      ) : null}
      <Table {...getTableProps()}>
        <MDBox component="thead">
          {headerGroups.map((headerGroup, key) => (
            <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, idx) => (
                <DataTableHeadCell
                  key={idx}
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width ? column.width : "auto"}
                  align={column.align ? column.align : "left"}
                  sorted={setSortedValue(column)}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </MDBox>
        <TableBody {...getTableBodyProps()}>
          {page.map((row, key) => {
            prepareRow(row);
            return (
              <TableRow
                key={key}
                {...row.getRowProps()}
                sx={{
                  // backgroundColor: key % 2 === 0 ? "#ffffff" : "#f7f9fc",
                  "&:hover": {
                    backgroundColor: "#e4ecffff", // light blue on hover
                    // cursor: "pointer",
                  },
                }}
              >
                {row.cells.map((cell, idx) => (
                  <DataTableBodyCell
                    key={idx}
                    noBorder={noEndBorder && rows.length - 1 === key}
                    align={cell.column.align ? cell.column.align : "left"}
                    {...cell.getCellProps()}
                    sx={{
                      "&:hover": {
                        fontWeight: "bold",
                        color: "black", // light blue on hover
                      },
                    }}
                  >
                    {cell.render("Cell")}
                  </DataTableBodyCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {page.length === 0 && (
        <MDBox
          py={5}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Icon sx={{ fontSize: "2rem !important", color: "text.disabled", mb: 0.5 }}>inbox</Icon>
          <MDTypography variant="body2" color="secondary">
            No records found
          </MDTypography>
        </MDBox>
      )}

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        p={!showTotalEntries && totalPages === 1 ? 0 : 3}
      >
        {showTotalEntries && (
          <MDBox mb={{ xs: 3, sm: 0 }}>
            <MDTypography variant="button" color="secondary" fontWeight="regular">
              Showing {entriesStart} to {entriesEnd} of {totalEntries} entries
            </MDTypography>
          </MDBox>
        )}
        {totalPages > 1 && (
          <MDPagination
            variant={pagination.variant ? pagination.variant : "gradient"}
            color={pagination.color ? pagination.color : "info"}
          >
            {(isServerSidePagination ? currentPage > 0 : canPreviousPage) && (
              <MDPagination
                item
                onClick={() => {
                  if (isServerSidePagination && pagination?.onPageChange) {
                    pagination.onPageChange(null, currentPage - 1);
                  } else {
                    previousPage();
                  }
                }}
              >
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </MDPagination>
            )}
            {renderPagination}
            {(isServerSidePagination ? currentPage < totalPages - 1 : canNextPage) && (
              <MDPagination
                item
                onClick={() => {
                  if (isServerSidePagination && pagination?.onPageChange) {
                    pagination.onPageChange(null, currentPage + 1);
                  } else {
                    nextPage();
                  }
                }}
              >
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </MDPagination>
            )}
          </MDPagination>
        )}
      </MDBox>
    </TableContainer>
  );
}

// Setting default values for the props of DataTable
DataTable.defaultProps = {
  entriesPerPage: { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch: false,
  showTotalEntries: true,
  pagination: { variant: "gradient", color: "info" },
  isSorted: true,
  noEndBorder: false,
};

// Typechecking props for the DataTable
DataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
      onChange: PropTypes.func,
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.objectOf(PropTypes.array).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
    page: PropTypes.number,
    onPageChange: PropTypes.func,
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  totalRecords: PropTypes.number,
};

export default DataTable;
